import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { 
  Receipt, Search, Clock, CheckCircle, XCircle, AlertTriangle, 
  Eye, Filter, RefreshCw, FileWarning, DollarSign, Calendar,
  TrendingUp, TrendingDown, AlertCircle, ExternalLink
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InvoiceControlPage = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showValidationDialog, setShowValidationDialog] = useState(false);

  const fetchData = async () => {
    try {
      const [invoicesRes, dashboardRes] = await Promise.all([
        fetch(`${API_URL}/api/invoice-control/invoices`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/invoice-control/dashboard`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (invoicesRes.ok) setInvoices(await invoicesRes.json());
      if (dashboardRes.ok) setDashboard(await dashboardRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchData();
  }, [token]);

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-blue-100 text-blue-700',
      validated: 'bg-cyan-100 text-cyan-700',
      variance_flagged: 'bg-orange-100 text-orange-700',
      dispute: 'bg-yellow-100 text-yellow-700',
      approved_for_payment: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
      paid: 'bg-emerald-100 text-emerald-700'
    };
    return <Badge className={styles[status] || 'bg-gray-100'}>{status?.replace(/_/g, ' ')}</Badge>;
  };

  const getVarianceBadge = (variance) => {
    if (!variance || variance === 0) return null;
    const isPositive = variance > 0;
    return (
      <Badge className={isPositive ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}>
        {isPositive ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
        {isPositive ? '+' : ''}£{variance.toLocaleString()}
      </Badge>
    );
  };

  const filteredInvoices = invoices.filter(inv => {
    if (filter === 'flagged' && inv.variance_flags?.length === 0) return false;
    if (filter !== 'all' && filter !== 'flagged' && inv.status !== filter) return false;
    if (searchQuery && 
        !inv.contractor_invoice_number?.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !inv.contractor_name?.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !inv.job_number?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const handleValidation = async (invoiceId, decision, notes) => {
    try {
      const res = await fetch(`${API_URL}/api/invoice-control/invoices/${invoiceId}/validate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ decision, notes })
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setShowValidationDialog(false);
        setSelectedInvoice(null);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to process validation');
      }
    } catch (error) {
      toast.error('Error processing validation');
    }
  };

  const handleMarkPaid = async (invoiceId) => {
    try {
      const res = await fetch(`${API_URL}/api/invoice-control/invoices/${invoiceId}/mark-paid`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Invoice marked as paid');
        fetchData();
      } else {
        toast.error('Failed to mark invoice as paid');
      }
    } catch (error) {
      toast.error('Error marking invoice as paid');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1F7A78]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Invoice Control Engine</h1>
          <p className="text-gray-500 text-xs sm:text-sm mt-1">Variance flagging and invoice validation</p>
        </div>
      </div>

      {/* KPI Cards */}
      {dashboard && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 md:gap-4">
          <Card className="cursor-pointer hover:border-blue-300" onClick={() => setFilter('pending')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Pending</p>
                  <p className="text-2xl font-semibold text-blue-600">{dashboard.counts?.pending_review || 0}</p>
                </div>
                <Clock className="w-6 h-6 text-blue-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:border-orange-300" onClick={() => setFilter('flagged')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Flagged</p>
                  <p className="text-2xl font-semibold text-orange-600">{dashboard.counts?.variance_flagged || 0}</p>
                </div>
                <AlertTriangle className="w-6 h-6 text-orange-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:border-yellow-300" onClick={() => setFilter('dispute')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Dispute</p>
                  <p className="text-2xl font-semibold text-yellow-600">{dashboard.counts?.in_dispute || 0}</p>
                </div>
                <FileWarning className="w-6 h-6 text-yellow-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:border-green-300" onClick={() => setFilter('approved_for_payment')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">To Pay</p>
                  <p className="text-2xl font-semibold text-green-600">{dashboard.counts?.approved_for_payment || 0}</p>
                </div>
                <CheckCircle className="w-6 h-6 text-green-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:border-red-300" onClick={() => setFilter('rejected')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Rejected</p>
                  <p className="text-2xl font-semibold text-red-600">{dashboard.counts?.rejected || 0}</p>
                </div>
                <XCircle className="w-6 h-6 text-red-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:border-emerald-300" onClick={() => setFilter('paid')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Paid</p>
                  <p className="text-2xl font-semibold text-emerald-600">{dashboard.counts?.paid || 0}</p>
                </div>
                <DollarSign className="w-6 h-6 text-emerald-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-amber-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Overdue</p>
                  <p className="text-2xl font-semibold text-amber-600">{dashboard.counts?.overdue || 0}</p>
                </div>
                <Calendar className="w-6 h-6 text-amber-400" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Value Summary */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          <Card>
            <CardContent className="p-3 md:p-4">
              <p className="text-xs md:text-sm text-gray-500">Pending Review</p>
              <p className="text-lg md:text-2xl font-bold text-blue-600">£{(dashboard.values?.pending_total || 0).toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 md:p-4">
              <p className="text-xs md:text-sm text-gray-500">Approved for Payment</p>
              <p className="text-lg md:text-2xl font-bold text-green-600">£{(dashboard.values?.approved_total || 0).toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 md:p-4">
              <p className="text-xs md:text-sm text-gray-500">Total Paid</p>
              <p className="text-lg md:text-2xl font-bold text-emerald-600">£{(dashboard.values?.paid_total || 0).toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-r from-gray-50 to-orange-50/30">
            <CardContent className="p-3 md:p-4">
              <p className="text-xs md:text-sm text-gray-500">Net Variance</p>
              <div className="flex items-center gap-2">
                <p className={`text-lg md:text-2xl font-bold ${(dashboard.variance?.net_variance || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {(dashboard.variance?.net_variance || 0) > 0 ? '+' : ''}£{(dashboard.variance?.net_variance || 0).toLocaleString()}
                </p>
                {dashboard.variance?.net_variance > 0 ? (
                  <TrendingUp className="w-4 h-4 md:w-5 md:h-5 text-red-500" />
                ) : (
                  <TrendingDown className="w-4 h-4 md:w-5 md:h-5 text-green-500" />
                )}
              </div>
              <p className="text-[10px] md:text-xs text-gray-400 mt-1">
                Over: £{(dashboard.variance?.total_over_budget || 0).toLocaleString()} | 
                Under: £{(dashboard.variance?.total_under_budget || 0).toLocaleString()}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-3 md:p-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-stretch sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search invoices..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-full sm:w-[200px]">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Invoices</SelectItem>
                  <SelectItem value="pending">Pending Review</SelectItem>
                  <SelectItem value="variance_flagged">Variance Flagged</SelectItem>
                  <SelectItem value="flagged">All Flagged</SelectItem>
                  <SelectItem value="dispute">In Dispute</SelectItem>
                  <SelectItem value="approved_for_payment">Approved for Payment</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={fetchData} className="shrink-0">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Invoices Table/Cards */}
      <Card>
        <CardHeader className="pb-2 md:pb-4">
          <CardTitle className="text-lg md:text-xl">Contractor Invoices ({filteredInvoices.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredInvoices.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Receipt className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>No invoices found</p>
            </div>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="md:hidden space-y-3">
                {filteredInvoices.map(invoice => (
                  <div 
                    key={invoice.id} 
                    className="border rounded-lg p-4 space-y-3 cursor-pointer hover:border-[#1F7A78] transition-colors"
                    onClick={() => setSelectedInvoice(invoice)}
                    data-testid={`invoice-card-${invoice.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-mono text-sm font-medium">{invoice.contractor_invoice_number}</p>
                        <p className="text-sm text-gray-600">{invoice.contractor_name || 'Unknown'}</p>
                        <p className="text-xs text-[#1F7A78]">{invoice.job_number}</p>
                      </div>
                      {getStatusBadge(invoice.status)}
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <div>
                        <span className="text-gray-500">Invoice:</span>
                        <span className="font-semibold ml-1">£{(invoice.total || 0).toLocaleString()}</span>
                      </div>
                      <div>
                        {getVarianceBadge(invoice.variance_amount)}
                      </div>
                    </div>
                    {(invoice.variance_flags || []).length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {invoice.variance_flags.map((flag, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {flag.replace(/_/g, ' ')}
                          </Badge>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center justify-between pt-2 border-t" onClick={(e) => e.stopPropagation()}>
                      <span className={`text-xs ${new Date(invoice.payment_due_date) < new Date() && invoice.status !== 'paid' ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                        Due: {invoice.payment_due_date}
                      </span>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={() => setSelectedInvoice(invoice)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        {['pending', 'variance_flagged'].includes(invoice.status) && ['admin', 'manager', 'director'].includes(user?.role) && (
                          <Button variant="ghost" size="sm" onClick={() => { setSelectedInvoice(invoice); setShowValidationDialog(true); }} className="text-green-600">
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                        )}
                        {invoice.status === 'approved_for_payment' && ['admin', 'manager'].includes(user?.role) && (
                          <Button variant="ghost" size="sm" onClick={() => handleMarkPaid(invoice.id)} className="text-emerald-600">
                            <DollarSign className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Invoice #</th>
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Contractor</th>
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Job</th>
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Status</th>
                    <th className="text-right p-3 text-sm font-medium text-gray-600">Invoice</th>
                    <th className="text-right p-3 text-sm font-medium text-gray-600">Approved</th>
                    <th className="text-center p-3 text-sm font-medium text-gray-600">Variance</th>
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Flags</th>
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Due Date</th>
                    <th className="text-right p-3 text-sm font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredInvoices.map(invoice => (
                    <tr 
                      key={invoice.id} 
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => setSelectedInvoice(invoice)}
                      data-testid={`invoice-row-${invoice.id}`}
                    >
                      <td className="p-3 font-mono text-sm">{invoice.contractor_invoice_number}</td>
                      <td className="p-3 text-sm">{invoice.contractor_name || 'Unknown'}</td>
                      <td className="p-3 text-sm text-[#1F7A78] hover:underline">{invoice.job_number}</td>
                      <td className="p-3">{getStatusBadge(invoice.status)}</td>
                      <td className="p-3 text-right font-semibold">£{(invoice.total || 0).toLocaleString()}</td>
                      <td className="p-3 text-right text-gray-500">£{(invoice.approved_amount || 0).toLocaleString()}</td>
                      <td className="p-3 text-center">
                        {getVarianceBadge(invoice.variance_amount)}
                        {invoice.variance_percent !== 0 && (
                          <span className="text-xs text-gray-400 block">{invoice.variance_percent}%</span>
                        )}
                      </td>
                      <td className="p-3">
                        <div className="flex flex-wrap gap-1">
                          {(invoice.variance_flags || []).map((flag, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {flag.replace(/_/g, ' ')}
                            </Badge>
                          ))}
                        </div>
                      </td>
                      <td className="p-3 text-sm">
                        <span className={new Date(invoice.payment_due_date) < new Date() && invoice.status !== 'paid' ? 'text-red-600 font-medium' : ''}>
                          {invoice.payment_due_date}
                        </span>
                      </td>
                      <td className="p-3 text-right">
                        <div className="flex justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => setSelectedInvoice(invoice)}
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {['pending', 'variance_flagged'].includes(invoice.status) && 
                           ['admin', 'manager', 'director'].includes(user?.role) && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => { setSelectedInvoice(invoice); setShowValidationDialog(true); }}
                              className="text-green-600"
                              title="Validate Invoice"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                          )}
                          {invoice.status === 'approved_for_payment' && 
                           ['admin', 'manager'].includes(user?.role) && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleMarkPaid(invoice.id)}
                              className="text-emerald-600"
                              title="Mark as Paid"
                            >
                              <DollarSign className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Invoice Details Dialog */}
      <Dialog open={!!selectedInvoice && !showValidationDialog} onOpenChange={() => setSelectedInvoice(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Invoice Details - {selectedInvoice?.contractor_invoice_number}</DialogTitle>
          </DialogHeader>
          {selectedInvoice && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <p>{getStatusBadge(selectedInvoice.status)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Required Action</p>
                  <p className="capitalize">{selectedInvoice.required_action?.replace(/_/g, ' ')}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Contractor</p>
                  <p className="font-medium">{selectedInvoice.contractor_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Job</p>
                  <div className="flex items-center gap-2">
                    <p>{selectedInvoice.job_number}</p>
                    {selectedInvoice.job_id && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-[#1F7A78] border-[#1F7A78] hover:bg-[#1F7A78] hover:text-white"
                        onClick={() => {
                          setSelectedInvoice(null);
                          navigate(`/fm/jobs/${selectedInvoice.job_id}`);
                        }}
                        data-testid="view-job-btn"
                      >
                        <ExternalLink className="w-3 h-3 mr-1" />
                        View Job
                      </Button>
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-500">PO Number</p>
                  <p>{selectedInvoice.po_number || <span className="text-red-500">NO PO</span>}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Payment Due</p>
                  <p className={new Date(selectedInvoice.payment_due_date) < new Date() && selectedInvoice.status !== 'paid' ? 'text-red-600 font-medium' : ''}>
                    {selectedInvoice.payment_due_date}
                  </p>
                </div>
              </div>

              {selectedInvoice.variance_flags?.length > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-orange-700 mb-2">
                    <AlertTriangle className="w-5 h-5" />
                    <span className="font-medium">Variance Flags</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedInvoice.variance_flags.map((flag, idx) => (
                      <Badge key={idx} className="bg-orange-100 text-orange-700">
                        {flag.replace(/_/g, ' ')}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {selectedInvoice.missing_documents?.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-yellow-700 mb-2">
                    <FileWarning className="w-5 h-5" />
                    <span className="font-medium">Missing Documents</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedInvoice.missing_documents.map((doc, idx) => (
                      <Badge key={idx} variant="outline" className="text-yellow-700">
                        {doc.replace(/_/g, ' ')}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span>Labour</span>
                  <span>£{(selectedInvoice.labour_total || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span>Materials</span>
                  <span>£{(selectedInvoice.materials_total || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span>£{(selectedInvoice.subtotal || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span>VAT</span>
                  <span>£{(selectedInvoice.vat_amount || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between font-bold border-t pt-2">
                  <span>Invoice Total</span>
                  <span>£{(selectedInvoice.total || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-gray-500">
                  <span>Approved Amount</span>
                  <span>£{(selectedInvoice.approved_amount || 0).toLocaleString()}</span>
                </div>
                <div className={`flex justify-between font-medium ${selectedInvoice.variance_amount > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  <span>Variance ({selectedInvoice.variance_percent}%)</span>
                  <span>{selectedInvoice.variance_amount > 0 ? '+' : ''}£{(selectedInvoice.variance_amount || 0).toLocaleString()}</span>
                </div>
              </div>

              {selectedInvoice.validation_history?.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2">Validation History</p>
                  <div className="space-y-2">
                    {selectedInvoice.validation_history.map((entry, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-sm border-l-2 border-gray-200 pl-3">
                        <div>
                          <span className="font-medium capitalize">{entry.action}</span>
                          <span className="text-gray-500"> by {entry.by}</span>
                          <span className="text-gray-400"> - {new Date(entry.at).toLocaleString()}</span>
                          {entry.notes && <p className="text-gray-600 mt-1">{entry.notes}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Validation Dialog */}
      <Dialog open={showValidationDialog} onOpenChange={setShowValidationDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Validate Invoice</DialogTitle>
          </DialogHeader>
          {selectedInvoice && (
            <InvoiceValidationForm 
              invoice={selectedInvoice}
              onValidate={(decision, notes) => handleValidation(selectedInvoice.id, decision, notes)}
              onCancel={() => { setShowValidationDialog(false); setSelectedInvoice(null); }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Validation Form Component
const InvoiceValidationForm = ({ invoice, onValidate, onCancel }) => {
  const [notes, setNotes] = useState('');

  return (
    <div className="space-y-4">
      <div className="bg-gray-50 rounded-lg p-4">
        <p className="font-medium">{invoice.contractor_invoice_number}</p>
        <p className="text-sm text-gray-500">{invoice.contractor_name} - {invoice.job_number}</p>
        <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
          <div>
            <span className="text-gray-500">Invoice:</span>
            <span className="font-semibold ml-2">£{(invoice.total || 0).toLocaleString()}</span>
          </div>
          <div>
            <span className="text-gray-500">Approved:</span>
            <span className="ml-2">£{(invoice.approved_amount || 0).toLocaleString()}</span>
          </div>
        </div>
        <div className={`mt-2 p-2 rounded ${invoice.variance_amount > 0 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
          Variance: {invoice.variance_amount > 0 ? '+' : ''}£{(invoice.variance_amount || 0).toLocaleString()} ({invoice.variance_percent}%)
        </div>
      </div>

      {invoice.variance_flags?.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {invoice.variance_flags.map((flag, idx) => (
            <Badge key={idx} className="bg-orange-100 text-orange-700">
              <AlertCircle className="w-3 h-3 mr-1" />
              {flag.replace(/_/g, ' ')}
            </Badge>
          ))}
        </div>
      )}

      <div>
        <label className="block text-sm font-medium mb-2">Notes</label>
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add validation notes..."
          rows={3}
        />
      </div>

      <div className="flex gap-2">
        <Button 
          className="flex-1 bg-green-600 hover:bg-green-700"
          onClick={() => onValidate('approve', notes)}
        >
          <CheckCircle className="w-4 h-4 mr-2" /> Approve
        </Button>
        <Button 
          variant="outline"
          className="flex-1 border-yellow-500 text-yellow-600 hover:bg-yellow-50"
          onClick={() => onValidate('query', notes)}
        >
          <AlertTriangle className="w-4 h-4 mr-2" /> Query
        </Button>
        <Button 
          variant="outline"
          className="flex-1 border-red-500 text-red-600 hover:bg-red-50"
          onClick={() => onValidate('reject', notes)}
        >
          <XCircle className="w-4 h-4 mr-2" /> Reject
        </Button>
      </div>
    </div>
  );
};

export default InvoiceControlPage;
